import { Observation } from "@medplum/fhirtypes";

import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { LaborVitalsFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/components/LaborVitalsFormFields";
import {
  LABOR_VITALS_BLOOD_PRESSURE_LINK_ID,
  LABOR_VITALS_DATE_TIME_LINK_ID,
  LABOR_VITALS_QUESTIONNAIRE_NAME,
  LABOR_VITALS_ROOT_LINK_ID,
  LABOR_VITALS_TEMPERATURE_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/constants/labor-vitals-link-ids";
import type { LaborVitalsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/types/labor-vitals-form";
import {
  getDefaultLaborVitalsFlowsheetFormValues,
  normalizeLaborVitalsFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/utils/labor-vitals-form-defaults";
import {
  mapLaborVitalsFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToLaborVitalsFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/utils/labor-vitals-questionnaire-mapping";
import { laborVitalsFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/zod-schemas/labor-vitals-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import { useLaborFlowsheetDrawerPersist } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

export type LaborVitalsFlowsheetDrawerMode = "quick-add" | "edit";

const LABOR_VITALS_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.LaborVitals];

interface LaborVitalsFlowsheetDrawerBaseProps {
  opened: boolean;
  onClose: () => void;
  clientId: string;
  encounterId: string;
  title?: string;
}

interface LaborVitalsQuickAddDrawerProps extends LaborVitalsFlowsheetDrawerBaseProps {
  mode: "quick-add";
  onRefresh?: () => Promise<void>;
}

interface LaborVitalsEditDrawerProps extends LaborVitalsFlowsheetDrawerBaseProps {
  mode: "edit";
  observation: Observation;
  questionnaireDisplayName?: string;
}

export type LaborVitalsFlowsheetDrawerProps =
  | LaborVitalsQuickAddDrawerProps
  | LaborVitalsEditDrawerProps;

function mergeLaborVitalsHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: LaborVitalsFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): LaborVitalsFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[LABOR_VITALS_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[LABOR_VITALS_DATE_TIME_LINK_ID] as
    | { vitalDate?: Date; vitalTime?: string }
    | undefined;
  const hydratedBloodPressure = hydratedRoot?.[LABOR_VITALS_BLOOD_PRESSURE_LINK_ID] as
    | { systolic?: string | number; diastolic?: string | number }
    | undefined;
  const hydratedTemperature = hydratedRoot?.[LABOR_VITALS_TEMPERATURE_LINK_ID] as
    | { temperatureValue?: string | number }
    | undefined;

  return {
    ...mapped,
    vitalDate: hydratedDateTime?.vitalDate ?? mapped.vitalDate,
    vitalTime: hydratedDateTime?.vitalTime ?? mapped.vitalTime,
    systolic: hydratedBloodPressure?.systolic ?? mapped.systolic,
    diastolic: hydratedBloodPressure?.diastolic ?? mapped.diastolic,
    temperatureValue: hydratedTemperature?.temperatureValue ?? mapped.temperatureValue,
  };
}

export function LaborVitalsFlowsheetDrawer(props: LaborVitalsFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<LaborVitalsFlowsheetFormValues>({
    initialValues: getDefaultLaborVitalsFlowsheetFormValues(),
    validate: zodResolver(laborVitalsFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<LaborVitalsFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: LABOR_VITALS_QUESTIONNAIRE_NAME,
    displayName: LABOR_VITALS_DISPLAY_NAME,
    timerKey: "vitals",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeLaborVitalsFlowsheetFormValues(laborVitalsFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultLaborVitalsFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapLaborVitalsFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToLaborVitalsFormValues,
    mergeHydratedEditFormValues: mergeLaborVitalsHydratedEditFormValues,
    questionnaireNotFoundMessage: "Labor vitals questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing labor vitals entry.",
    editContext:
      mode === "edit"
        ? {
            observation: props.observation,
            questionnaireDisplayName: props.questionnaireDisplayName,
          }
        : undefined,
    onRefresh: mode === "quick-add" ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.LaborVitals}`}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <LaborVitalsFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
